<template>
  <div 
    class="postit-note"
    :class="[`postit-${note.color}`, { 'dragging': isDragging }]"
    :style="{ width: note.width + 'px', height: note.height + 'px' }"
    draggable="true"
    @dragstart="onDragStart"
    @dragend="onDragEnd"
    @click="onClick"
    @dblclick="onDoubleClick"
    @mouseenter="showControls = true"
    @mouseleave="showControls = false"
  >
    <!-- Post-it gölgesi -->
    <div class="postit-shadow"></div>
    
    <!-- Post-it kağıt -->
    <div class="postit-paper">
      <!-- Pin iğnesi -->
      <div class="postit-pin">
        <div class="pin-head"></div>
        <div class="pin-needle"></div>
      </div>
      
      <!-- Üst bantı -->
      <div class="postit-tape"></div>
      
      <!-- Not içeriği -->
      <div class="postit-content">
        <!-- Başlık -->
        <div class="postit-title" v-if="note.title">{{ note.title }}</div>
        
        <!-- Özet metin -->
        <div class="postit-text" v-html="formattedText"></div>
        
        <!-- Tarih bilgisi -->
        <div class="postit-date">
          {{ formatDate(note.updatedAt || note.createdAt) }}
        </div>
      </div>
      
      <!-- Kontrol butonları -->
      <div class="postit-controls" v-show="showControls">
        <a-button 
          type="link" 
          size="small" 
          @click.stop="editNote"
          class="control-btn edit-btn"
        >
          <a-icon type="edit" />
        </a-button>
        <a-button 
          type="link" 
          size="small" 
          @click.stop="deleteNote"
          class="control-btn delete-btn"
        >
          <a-icon type="delete" />
        </a-button>
      </div>
      
      <!-- Köşe kıvrımı -->
      <div class="postit-corner"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PostitNote',
  props: {
    note: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isDragging: false,
      showControls: false
    }
  },
  computed: {
    formattedText() {
      if (!this.note.text) {
        return '<span class="empty-text">Boş not - düzenlemek için çift tıklayın</span>'
      }
      // Metni maksimum 80 karakter ile sınırla (özet için)
      let text = this.note.text
      if (text.length > 80) {
        text = text.substring(0, 80) + '...'
      }
      // Satır sonlarını <br> ile değiştir
      return text.replace(/\n/g, '<br>')
    }
  },
  methods: {
    onDragStart(event) {
      this.isDragging = true
      this.$emit('drag-start', this.note)
      
      // Gerçekçi ghost image oluştur
      const dragImage = this.$el.cloneNode(true)
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-10000px'
      dragImage.style.left = '-10000px'
      dragImage.style.transform = 'rotate(0deg) scale(0.9)'
      dragImage.style.opacity = '0.85'
      dragImage.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.3)'
      dragImage.style.zIndex = '99999'
      dragImage.style.pointerEvents = 'none'
      
      // Ana elementi gizle (ghost gibi görünsün)
      this.$el.style.opacity = '0.3'
      this.$el.style.transform = this.$el.style.transform + ' scale(0.95)'
      
      document.body.appendChild(dragImage)
      
      // Drag image'ı ayarla
      event.dataTransfer.setDragImage(dragImage, this.note.width / 2, this.note.height / 2)
      
      // Ghost image'ı temizle
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage)
        }
      }, 0)
    },
    
    onDragEnd() {
      this.isDragging = false
      this.$emit('drag-end')
      
      // Ana elementin stilini geri yükle
      this.$el.style.opacity = '1'
      const currentTransform = this.$el.style.transform
      this.$el.style.transform = currentTransform.replace('scale(0.95)', '')
    },
    
    onClick() {
      // Tek tıklamada hiçbir şey yapma, sadece çift tıklama ile açılsın
    },
    
    onDoubleClick() {
      if (!this.isDragging) {
        this.editNote()
      }
    },
    
    editNote() {
      this.$emit('edit', this.note)
    },
    
    deleteNote() {
      this.$confirm({
        title: 'Notu Sil',
        content: 'Bu notu silmek istediğinizden emin misiniz?',
        okText: 'Sil',
        okType: 'danger',
        cancelText: 'İptal',
        onOk: () => {
          this.$emit('delete', this.note.id)
        }
      })
    },
    
    formatDate(date) {
      if (!date) return ''
      const now = new Date()
      const noteDate = new Date(date)
      const diffMs = now - noteDate
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)
      
      if (diffMins < 1) return 'Şimdi'
      if (diffMins < 60) return `${diffMins} dk önce`
      if (diffHours < 24) return `${diffHours} saat önce`
      if (diffDays < 7) return `${diffDays} gün önce`
      
      return noteDate.toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.postit-note {
  position: absolute;
  cursor: grab;
  z-index: 5;
  transition: all 0.2s ease;
  
  &:hover {
    z-index: 15;
    transform: scale(1.05) !important;
  }
  
  &.dragging {
    cursor: grabbing;
    z-index: 20;
    transition: none; // Dragging sırasında transition'ı kaldır
  }
  
  &:active {
    cursor: grabbing;
  }
}

.postit-shadow {
  position: absolute;
  top: 6px;
  left: 6px;
  right: -6px;
  bottom: -6px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  filter: blur(4px);
  z-index: -1;
}

.postit-paper {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 2px;
  box-shadow: 
    0 4px 8px rgba(0, 0, 0, 0.15),
    0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: repeating-linear-gradient(
      transparent,
      transparent 24px,
      rgba(0, 0, 0, 0.05) 25px
    );
    pointer-events: none;
  }
}

.postit-pin {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 20;
  
  .pin-head {
    position: relative;
    width: 8px;
    height: 8px;
    background: radial-gradient(circle, #ff6b6b 30%, #e55555 70%);
    border-radius: 50%;
    box-shadow: 
      0 1px 3px rgba(0, 0, 0, 0.4),
      inset 0 1px 1px rgba(255, 255, 255, 0.3);
    
    &::before {
      content: '';
      position: absolute;
      top: 1px;
      left: 1px;
      width: 3px;
      height: 3px;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 50%;
    }
  }
  
  .pin-needle {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    height: 8px;
    background: linear-gradient(to bottom, #999 0%, #666 100%);
    box-shadow: 0 0 1px rgba(0, 0, 0, 0.3);
  }
}

.postit-tape {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 30px;
    height: 2px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 1px;
  }
}

.postit-content {
  padding: 25px 15px 15px 20px; // Sol padding artırıldı pin için
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.postit-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin-bottom: 8px;
  line-height: 1.2;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.postit-text {
  flex: 1;
  font-size: 13px;
  line-height: 1.4;
  color: #444;
  word-wrap: break-word;
  overflow: hidden;
  
  .empty-text {
    color: #999;
    font-style: italic;
    opacity: 0.7;
  }
}

.postit-date {
  margin-top: auto;
  font-size: 11px;
  color: #666;
  text-align: right;
  opacity: 0.8;
}

.postit-controls {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  
  .control-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    
    &.edit-btn {
      color: #1890ff;
      
      &:hover {
        background: #e6f7ff;
        color: #1890ff;
      }
    }
    
    &.delete-btn {
      color: #ff4d4f;
      
      &:hover {
        background: #fff2f0;
        color: #ff4d4f;
      }
    }
  }
}

.postit-corner {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 15px;
  height: 15px;
  background: linear-gradient(-45deg, transparent 46%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.05) 100%);
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 0;
    height: 0;
    border-left: 12px solid transparent;
    border-bottom: 12px solid rgba(0, 0, 0, 0.05);
  }
}

// Renk temaları
.postit-yellow {
  .postit-paper {
    background: linear-gradient(135deg, #ffeb3b 0%, #fff176 50%, #ffee58 100%);
  }
}

.postit-pink {
  .postit-paper {
    background: linear-gradient(135deg, #ff4081 0%, #f8bbd9 50%, #fce4ec 100%);
  }
}

.postit-blue {
  .postit-paper {
    background: linear-gradient(135deg, #2196f3 0%, #90caf9 50%, #e3f2fd 100%);
  }
}

.postit-green {
  .postit-paper {
    background: linear-gradient(135deg, #4caf50 0%, #a5d6a7 50%, #e8f5e8 100%);
  }
}

.postit-orange {
  .postit-paper {
    background: linear-gradient(135deg, #ff9800 0%, #ffcc02 50%, #fff3c4 100%);
  }
}

.postit-purple {
  .postit-paper {
    background: linear-gradient(135deg, #9c27b0 0%, #ce93d8 50%, #f3e5f5 100%);
  }
}
</style> 